import { Grid, Stack } from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import AcxButton from "components/UI/AcxButton";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSlider from "components/UI/AcxSlider";
import AcxMenu, {
    AcxMenuItemProps,
    StyledMenuLabel,
} from "components/UI/Menu/AcxMenu";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useStore } from "utils/useStore";
import { ConversationFilters } from "../ConversationFilters";
import { DataLabelingModuleAndQuestionSelectors } from "../DataLabelingModuleAndQuestionSelector";
import { LabeledConversationsChip } from "../LabeledConversationsChip";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { LLMClassifierBuilderStepperStore } from "../../Stores/LLMClassifierBuilderStepperStore";

/** Chooses what to test the model against and starts the test. */
export const TestModelSection = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);

    return (
        <TitledSection
            title="Test Model"
            subtitle={
                store.isOnConversationsTesting()
                    ? "Select filters to narrow down conversations you want to test against"
                    : "Select a module and question to test this model against"
            }
            variant="bordered"
            sx={{ width: "100%" }}
        >
            {store.isAgreementTesting() && (
                <>
                    <DataLabelingModuleAndQuestionSelectors />
                    {store.classifier.implementation.isLogicChain() && (
                        <Grid
                            container
                            item
                            direction="row"
                            paddingTop={1}
                            columnGap={1}
                        >
                            {store.classifier.outputs.map((output) => (
                                <Grid item key={output.result}>
                                    <LabeledConversationsChip
                                        result={output.result}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            )}
            {store.isOnConversationsTesting() && (
                <ConversationFilters
                    filters={store.testOnConversationsFilters}
                />
            )}
            <TestModelButtons />
        </TitledSection>
    );
});

type TestModelButtonProps = React.ComponentPropsWithoutRef<typeof AcxButton> & {
    sampleSize?: number;
};

const TestModelButton = observer(function (props: TestModelButtonProps) {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);

    return (
        <AcxButton
            {...props}
            onClick={(event) => {
                if (props.onClick) props.onClick(event);
                else {
                    if (store.isOnConversationsTesting()) {
                        store.testLLMClassifierOnConversations(
                            props.sampleSize,
                        );
                    } else {
                        store.testLLMClassifierAgreement({
                            sampleSize: props.sampleSize,
                        });
                    }
                    stepper.setCurrentWizardStep("testing");
                }
            }}
            disabled={!store.isLLMClassifierTestable() || props.disabled}
        >
            {props.children}
        </AcxButton>
    );
});

const TestModelButtons = observer(function () {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);

    const hasTested =
        !!store.testAgreementResponse || !!store.testOnConversationsResponse;
    const testingWord = hasTested ? "Retest" : "Test";

    const [isNTestDialogOpen, setIsNTestDialogOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [sampleSize, setSampleSize] = useState(50);
    const sampleSizeLimit = Math.max(1, store.testSampleSizeLimit);
    const testAllCount = store.testAllCount;
    const hasNothingToTest = testAllCount === 0;
    const sampleSizeToUse = Math.min(sampleSize, sampleSizeLimit);
    const isSampleSizeOne = sampleSizeToUse === 1;
    // The most either test runs, once the counts are loaded.
    const testLimit = testAllCount ?? sampleSizeLimit;
    const menuItems = store.classifier.outputs.map<AcxMenuItemProps>(
        (output) => ({
            id: "menu-item-test-one-result",
            props: {
                onClick: () => {
                    setMenuAnchor(null);
                    store.testLLMClassifierAgreement({
                        isTestOne: true,
                        testOneLabel: output.result,
                    });
                    stepper.setCurrentWizardStep("testing");
                },
            },
            label: <StyledMenuLabel>Test {output.result}</StyledMenuLabel>,
        }),
    );

    return (
        <Grid
            container
            item
            columnGap={1}
            marginTop={2}
            justifyContent="flex-end"
        >
            <AcxMenu
                anchorElement={menuAnchor}
                menuItems={menuItems}
                onMenuClose={() => setMenuAnchor(null)}
            />
            {store.isAgreementTesting() && (
                <AcxButton
                    onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                    }}
                    disabled={!store.isLLMClassifierTestable()}
                >
                    {testingWord} one
                </AcxButton>
            )}
            {store.isOnConversationsTesting() && (
                <TestModelButton sampleSize={1}>
                    {testingWord} one
                </TestModelButton>
            )}
            <AcxButton
                disabled={!store.isLLMClassifierTestable() || hasNothingToTest}
                onClick={() => setIsNTestDialogOpen(true)}
            >
                {testingWord} n (up to {testLimit})
            </AcxButton>
            <TestModelButton
                sampleSize={LLMClassifierBuilderStore.MaxModelTestSampleSize}
                disabled={hasNothingToTest}
            >
                {testingWord} all (up to {testLimit})
            </TestModelButton>
            {hasTested && (
                <AcxButton
                    color="white"
                    onClick={() => stepper.setCurrentWizardStep("testing")}
                >
                    View last test results
                </AcxButton>
            )}
            <ConfirmationDialog
                onClose={() => setIsNTestDialogOpen(false)}
                onCancel={() => setIsNTestDialogOpen(false)}
                isOpen={isNTestDialogOpen}
                confirmButtonText={`Test ${sampleSizeToUse}`}
                onConfirm={() => {
                    setIsNTestDialogOpen(false);
                    if (store.isOnConversationsTesting()) {
                        store.testLLMClassifierOnConversations(sampleSizeToUse);
                    } else {
                        store.testLLMClassifierAgreement({
                            sampleSize: sampleSizeToUse,
                        });
                    }
                    stepper.setCurrentWizardStep("testing");
                }}
                content={
                    <Stack direction="column" rowGap={2}>
                        <AcxSlider
                            value={sampleSizeToUse}
                            onChange={(value) => {
                                setSampleSize(value);
                            }}
                            underSlideLabel={`${
                                isSampleSizeOne ? "" : "Up to "
                            }${sampleSizeToUse} conversation${
                                isSampleSizeOne ? "" : "s"
                            }${store.isAgreementTesting() ? " per tag" : ""}`}
                            minValue={1}
                            maxValue={sampleSizeLimit}
                        />
                        or
                        <AcxMainTextField
                            id="sample-size-input"
                            labelText="Input number of conversations"
                            value={sampleSizeToUse}
                            placeholderText={`Enter sample size, max ${sampleSizeLimit}`}
                            type="number"
                            insertBlankIfNumber
                            onChange={(event) => {
                                let value = event.target.valueAsNumber;
                                if (!value || isNaN(value) || value < 0) return;
                                setSampleSize(Math.min(value, sampleSizeLimit));
                            }}
                        />
                    </Stack>
                }
            />
        </Grid>
    );
});
